import React from "react";
import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import { IconButton } from "components/IconButton/IconButton";
import { useTranslate } from "hooks/useTranslate";

const enum NodeVisibilityStatus {
  VISIBLE = "ACTIVE",
  PARTIALLY_VISIBLE = "PARTIALLY_VISIBLE",
  HIDDEN = "HIDDEN",
}

type OnClickProp = {
  onClick?: () => void;
};

type NodeVisibilityProps = OnClickProp & {
  node: NodeApi;
};

export const NodeVisibility = ({ node, onClick }: NodeVisibilityProps) => {
  const status = getNodeVisibilityStatus(node);
  return (
    <NodeVisibilityIcon
      status={status}
      id={`node-visibility-${node.id}`}
      onClick={onClick}
    />
  );
};

const LIST_STATUS_PARTIALLY_VISIBLE = "list-status-partially-visible";
const LIST_STATUS_DEFAULT = "list-status-default";
const COLOR_PRIMARY = "blue-300";
const COLOR_SECONDARY = "gray-100";

type NodeVisibilityIconProps = OnClickProp & {
  status: NodeVisibilityStatus;
  id: string;
};

const NodeVisibilityIcon = ({
  status,
  id,
  onClick,
}: NodeVisibilityIconProps) => {
  const translate = useTranslate();

  let iconName: string = "";
  let iconColor: string = "";
  let tooltipText: string = "";

  switch (status) {
    case NodeVisibilityStatus.HIDDEN:
      iconName = LIST_STATUS_DEFAULT;
      iconColor = COLOR_SECONDARY;
      tooltipText = translate("NODE_VISIBILITY_ACTIVATE") as string;
      break;
    case NodeVisibilityStatus.PARTIALLY_VISIBLE:
      iconName = LIST_STATUS_PARTIALLY_VISIBLE;
      iconColor = COLOR_PRIMARY;
      tooltipText = translate("NODE_VISIBILITY_DEACTIVATE") as string;
      break;
    default:
      iconName = LIST_STATUS_DEFAULT;
      iconColor = COLOR_PRIMARY;
      tooltipText = translate("NODE_VISIBILITY_DEACTIVATE") as string;
  }

  return (
    <IconButton
      id={id}
      iconPrefix="trox"
      className="border-0"
      iconClassName={`icon-16 text-${iconColor}`}
      icon={iconName}
      onClick={onClick}
      tooltipText={tooltipText}
    />
  );
};

const getNodeVisibilityStatus = (node: NodeApi) => {
  const {
    data: { active, partiallyVisible },
  } = node;
  if (active && !partiallyVisible) {
    return NodeVisibilityStatus.VISIBLE;
  }
  if (active && partiallyVisible) {
    return NodeVisibilityStatus.PARTIALLY_VISIBLE;
  }
  return NodeVisibilityStatus.HIDDEN;
};
